import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  attachmentFileKey,
  buildGroupAddressesForProduct,
  computeGroupedProducts,
  computeProductTableBodyRenderOrder,
  getAddToGroupOptions,
  getBlankLocationAddress,
  getCatalogSourceDropdownOptions,
  getCatalogSourceOptions,
  getGroupAnchorIndexId,
  getGroupLosingMoneyFlags,
  getLosingMoneyRowSx,
  getProductGroupId,
  getSelectedLineCatalogCount,
  getSelectedLineCatalogProducts,
  getValidUngroupedSelectionIds,
  GROUP_QUANTITY_MODE_LEGACY,
  GROUP_QUANTITY_MODE_SET_MULTIPLIER,
  isQuotationChooseCatalogRow,
  isSameGroupId,
  normalizeGroupLineQuantity,
  normalizeGroupNameKey,
  quotationGroupNameExists,
  snapshotGroupMemberMarketing,
  withInheritedGroupNotes,
} from './quotationGroupingHelpers'
import { clearCatalogInstallCost } from 'src/utils/retailPriceEdit'

export function useQuotationProductGrouping({
  selectedProducts,
  setSelectedProducts,
  markupType,
  theme,
  enqueueSnackbar,
  productAttachments,
  setProductAttachments,
  explodedPermissions,
  hasMarkupPermission,
  setRefreshCalculation,
  remapProductIndexIds,
  negativeMarketingPermissionNames = ['Allow Negative Marketing Adjustment Estimate'],
  groupQuantityMode = GROUP_QUANTITY_MODE_LEGACY,
}) {
  const [customerOrderProductGroups, setCustomerOrderProductGroups] = useState([])
  const [openGroupedRows, setOpenGroupedRows] = useState({})
  const [groupEditMode, setGroupEditMode] = useState({})
  const [groupLocationStates, setGroupLocationStates] = useState({})
  const [groupSelectedProductIds, setGroupSelectedProductIds] = useState([])
  const [groupProductQuantityDrafts, setGroupProductQuantityDrafts] = useState({})
  const [groupProductMarketingValues, setGroupProductMarketingValues] = useState({})
  const [groupInstallMarketingValues, setGroupInstallMarketingValues] = useState({})
  const [addToGroupId, setAddToGroupId] = useState(null)
  const [openAddToGroupModal, setOpenAddToGroupModal] = useState(false)
  const [openGroupCatalogSourceModal, setOpenGroupCatalogSourceModal] = useState(false)
  const [groupCatalogSourceIndexId, setGroupCatalogSourceIndexId] = useState(null)
  const [groupCatalogCustomSourceId, setGroupCatalogCustomSourceId] = useState(null)
  const [customCatalogGroupSources, setCustomCatalogGroupSources] = useState([])
  const [groupCatalogSourceInputValue, setGroupCatalogSourceInputValue] = useState('')
  const [showGroupCatalogCreateForm, setShowGroupCatalogCreateForm] = useState(false)
  const [groupCatalogCustomName, setGroupCatalogCustomName] = useState('')
  const [groupCatalogCustomDescription, setGroupCatalogCustomDescription] = useState('')
  const [groupCatalogCreateError, setGroupCatalogCreateError] = useState('')
  const [groupNoteTargets, setGroupNoteTargets] = useState({})
  const [openGroupProductNoteIndex, setOpenGroupProductNoteIndex] = useState(null)
  const [groupInstallNoteTargets, setGroupInstallNoteTargets] = useState({})
  const [openGroupInstallNoteIndex, setOpenGroupInstallNoteIndex] = useState(null)

  const groupedProducts = useMemo(
    () =>
      computeGroupedProducts({
        selectedProducts,
        markupType,
        customerOrderProductGroups,
        groupProductMarketingValues,
        groupInstallMarketingValues,
        groupQuantityMode,
      }),
    [
      selectedProducts,
      markupType,
      customerOrderProductGroups,
      groupProductMarketingValues,
      groupInstallMarketingValues,
      groupQuantityMode,
    ],
  )

  const groupedProductsMap = useMemo(() => {
    return new Map((groupedProducts || []).map((g) => [String(g.id), g]))
  }, [groupedProducts])

  const getGroupLocationQuantity = useCallback(
    (groupId, fallbackProduct) => {
      if (groupQuantityMode === GROUP_QUANTITY_MODE_SET_MULTIPLIER) {
        const group = groupedProductsMap.get(String(groupId))
        const fromGroup = Number(group?.groupQuantity)
        if (fromGroup > 0) return fromGroup
        const meta = (customerOrderProductGroups || []).find((g) =>
          isSameGroupId(g?.id ?? g?.group_id, groupId),
        )
        const fromMeta = Number(
          meta?.product_marketing_quantity ?? meta?.productMarketingQuantity,
        )
        if (fromMeta > 0) return fromMeta
      }
      return Math.max(1, Number(fallbackProduct?.locationQuantity || 1))
    },
    [groupQuantityMode, groupedProductsMap, customerOrderProductGroups],
  )

  const patchGroupLocationMeta = useCallback((groupId, patch) => {
    setCustomerOrderProductGroups((prev) => {
      const list = prev || []
      const idx = list.findIndex((group) =>
        isSameGroupId(group?.id ?? group?.group_id, groupId),
      )
      if (idx === -1) {
        return [
          ...list,
          {
            id: groupId,
            group_id: groupId,
            ...patch,
          },
        ]
      }
      return list.map((group, groupIndex) =>
        groupIndex !== idx ? group : { ...group, ...patch },
      )
    })
  }, [])

  const productTableBodyRenderOrder = useMemo(
    () => computeProductTableBodyRenderOrder(selectedProducts, groupedProducts),
    [selectedProducts, groupedProducts],
  )

  const addToGroupOptions = useMemo(
    () =>
      getAddToGroupOptions({
        groupedProducts,
        customerOrderProductGroups,
        selectedProducts,
      }),
    [customerOrderProductGroups, groupedProducts, selectedProducts],
  )

  // Drop stale checkbox selections. While editing an existing group, grouped members
  // are valid selections; outside edit mode only ungrouped selections are valid.
  useEffect(() => {
    setGroupSelectedProductIds((prev) => {
      const isEditingGroup = Object.values(groupEditMode || {}).some(Boolean)
      const next = isEditingGroup
        ? (prev || []).filter((id) =>
            (selectedProducts || []).some(
              (product) =>
                Number(product?.indexId) === Number(id) &&
                isQuotationChooseCatalogRow(product),
            ),
          )
        : getValidUngroupedSelectionIds(selectedProducts, prev)
      if (
        next.length === (prev || []).length &&
        next.every((id, i) => Number(id) === Number(prev[i]))
      ) {
        return prev
      }
      return next
    })
  }, [selectedProducts, groupEditMode])

  const getGroupMemberIndexIdsByProductIndexId = useCallback(
    (productIndexId) => {
      const numId = Number(productIndexId)
      if (Number.isNaN(numId)) return []

      // Prefer group map membership so nested rows always resolve siblings.
      for (const group of groupedProductsMap.values()) {
        const ids = (group?.products || [])
          .map((p) => Number(p.indexId))
          .filter((id) => !Number.isNaN(id))
        if (ids.includes(numId) && ids.length) return ids
      }

      const product = (selectedProducts || []).find((p) => Number(p?.indexId) === numId)
      const gid = getProductGroupId(product)
      if (gid == null || gid === '') return [numId]
      const group = groupedProductsMap.get(String(gid))
      if (group?.products?.length) {
        const ids = group.products
          .map((p) => Number(p.indexId))
          .filter((id) => !Number.isNaN(id))
        if (ids.length) return ids
      }
      const ids = (selectedProducts || [])
        .filter((p) => String(getProductGroupId(p) || '') === String(gid))
        .map((p) => Number(p.indexId))
        .filter((id) => !Number.isNaN(id))
      return ids.length ? ids : [numId]
    },
    [selectedProducts, groupedProductsMap],
  )

  const getProductAttachmentsList = useCallback(
    (productIndexId) => {
      const numId = Number(productIndexId)
      const memberIds = getGroupMemberIndexIdsByProductIndexId(numId)
      if (memberIds.length <= 1) {
        return productAttachments[numId] || productAttachments[String(numId)] || []
      }
      const seen = new Set()
      const merged = []
      memberIds.forEach((mid) => {
        const list = productAttachments[mid] || productAttachments[String(mid)] || []
        list.forEach((f, fileIdx) => {
          const key = attachmentFileKey(f) || `__group_att_${mid}_${fileIdx}`
          if (!seen.has(key)) {
            seen.add(key)
            merged.push(f)
          }
        })
      })
      return merged
    },
    [productAttachments, getGroupMemberIndexIdsByProductIndexId],
  )

  const clearGroupSelectionForProduct = useCallback((indexId) => {
    setGroupSelectedProductIds((prev) =>
      (prev || []).filter((id) => Number(id) !== Number(indexId)),
    )
  }, [])

  const remapGroupStateOnIndexIdChange = useCallback((indexIdMap) => {
    setGroupSelectedProductIds((prev) =>
      (prev || []).map((pid) => indexIdMap[String(pid)] ?? pid),
    )
  }, [])

  const handleOpenGroupNote = useCallback(
    (indexId) => {
      const key = String(indexId)
      setGroupNoteTargets((prev) => ({
        ...prev,
        [key]: getGroupMemberIndexIdsByProductIndexId(indexId),
      }))
      setOpenGroupProductNoteIndex(indexId)
    },
    [getGroupMemberIndexIdsByProductIndexId],
  )

  const handleOpenGroupInstallNote = useCallback(
    (indexId) => {
      const key = String(indexId)
      setGroupInstallNoteTargets((prev) => ({
        ...prev,
        [key]: getGroupMemberIndexIdsByProductIndexId(indexId),
      }))
      setOpenGroupInstallNoteIndex(indexId)
    },
    [getGroupMemberIndexIdsByProductIndexId],
  )

  const handleGroupFieldChange = (groupId, field, value) => {
    setSelectedProducts((prev) =>
      (prev || []).map((product) => {
        if (!isSameGroupId(product.group_id, groupId)) return product
        if (field === 'name') return { ...product, group_name: value }
        if (field === 'description') return { ...product, group_description: value }
        return product
      }),
    )
    setCustomerOrderProductGroups((prev) =>
      (prev || []).map((group) => {
        if (!isSameGroupId(group?.id ?? group?.group_id, groupId)) return group
        if (field === 'name') {
          return { ...group, name: value, group_name: value }
        }
        if (field === 'description') {
          return { ...group, description: value, group_description: value }
        }
        return group
      }),
    )
  }

  const handleGroupQuantityChange = (groupId, value) => {
    let newValue = String(value ?? '')
    if (newValue.startsWith('-')) newValue = newValue.slice(1)
    newValue = newValue.replace(/[^0-9]/g, '')
    if (newValue === '' || Number(newValue) <= 0) newValue = 1
    else newValue = Number(newValue)

    if (groupQuantityMode === GROUP_QUANTITY_MODE_SET_MULTIPLIER) {
      setCustomerOrderProductGroups((prev) => {
        const list = prev || []
        const idx = list.findIndex((group) =>
          isSameGroupId(group?.id ?? group?.group_id, groupId),
        )
        const existingGroup = idx >= 0 ? list[idx] : null
        const existingProdQty = Number(
          existingGroup?.product_marketing_quantity ??
            existingGroup?.productMarketingQuantity ??
            0,
        )
        const existingInstQty = Number(
          existingGroup?.install_marketing_quantity ??
            existingGroup?.installMarketingQuantity ??
            0,
        )
        const hasManualInstallQty =
          Boolean(existingGroup?.install_qty_manually_set) ||
          (existingProdQty > 0 &&
            existingInstQty > 0 &&
            existingProdQty !== existingInstQty) ||
          (selectedProducts || []).some(
            (product) =>
              isSameGroupId(product.group_id, groupId) &&
              product?.manually_updated_install_quantity,
          )
        if (idx === -1) {
          return [
            ...list,
            {
              id: groupId,
              group_id: groupId,
              product_marketing_quantity: Number(newValue),
              ...(hasManualInstallQty
                ? {}
                : { install_marketing_quantity: Number(newValue) }),
            },
          ]
        }
        return list.map((group, groupIndex) =>
          groupIndex !== idx
            ? group
            : {
                ...group,
                product_marketing_quantity: Number(newValue),
                ...(hasManualInstallQty
                  ? {}
                  : { install_marketing_quantity: Number(newValue) }),
              },
        )
      })
      // Expand/shrink group locations to match set qty (Multiple mode).
      setSelectedProducts((prevProducts) => {
        const groupProducts = (prevProducts || []).filter((product) =>
          isSameGroupId(product.group_id, groupId),
        )
        if (!groupProducts.length) return prevProducts
        const representative = groupProducts[0]
        const sourceAddr = representative?.location || representative?.addresses || []
        const templateAddresses = buildGroupAddressesForProduct(
          { ...representative, locationQuantity: Number(newValue) },
          sourceAddr,
          {
            allLocationSame: representative?.allLocationSame,
            isSingleCard: representative?.is_single_card,
            locationQuantity: Number(newValue),
          },
        )
        setCustomerOrderProductGroups((prev) => {
          const list = prev || []
          const idx = list.findIndex((group) =>
            isSameGroupId(group?.id ?? group?.group_id, groupId),
          )
          if (idx === -1) return prev
          return list.map((group, groupIndex) =>
            groupIndex !== idx
              ? group
              : {
                  ...group,
                  group_addresses: templateAddresses,
                },
          )
        })
        return (prevProducts || []).map((product) => {
          if (!isSameGroupId(product.group_id, groupId)) return product
          return {
            ...product,
            location: buildGroupAddressesForProduct(
              { ...product, locationQuantity: Number(newValue) },
              templateAddresses,
              {
                allLocationSame: product?.allLocationSame,
                isSingleCard: product?.is_single_card,
                locationQuantity: Number(newValue),
              },
            ),
          }
        })
      })
      setRefreshCalculation((v) => !v)
      return
    }

    setSelectedProducts((prevProducts) => {
      const groupProducts = (prevProducts || []).filter((product) =>
        isSameGroupId(product.group_id, groupId),
      )
      if (!groupProducts.length) return prevProducts

      const representative = groupProducts[0]
      const sourceAddr = representative?.location || representative?.addresses || []
      const templateAddresses = buildGroupAddressesForProduct(
        { ...representative, locationQuantity: newValue },
        sourceAddr,
        {
          allLocationSame: representative?.allLocationSame,
          isSingleCard: representative?.is_single_card,
        },
      )

      return (prevProducts || []).map((product) => {
        if (!isSameGroupId(product.group_id, groupId)) return product

        // Same as ungrouped: once install qty is manually edited, stop auto-syncing
        // even if install qty currently equals group qty.
        const nextInstallQty = product.manually_updated_install_quantity
          ? Number(
              product.install_marketing_quantity ??
                product.install_quantity ??
                product.group_install_quantity ??
                1,
            )
          : Number(newValue)

        return {
          ...product,
          locationQuantity: Number(newValue),
          product_marketing_quantity: Number(newValue),
          group_product_quantity: Number(newValue),
          install_quantity: nextInstallQty,
          install_marketing_quantity: nextInstallQty,
          group_install_quantity: nextInstallQty,
          location: buildGroupAddressesForProduct(product, templateAddresses, {
            allLocationSame: product?.allLocationSame,
            isSingleCard: product?.is_single_card,
          }),
          addresses: buildGroupAddressesForProduct(product, templateAddresses, {
            allLocationSame: product?.allLocationSame,
            isSingleCard: product?.is_single_card,
          }),
        }
      })
    })

    setCustomerOrderProductGroups((prev) => {
      const list = prev || []
      const idx = list.findIndex((group) =>
        isSameGroupId(group?.id ?? group?.group_id, groupId),
      )
      const hasManualInstallQty = (selectedProducts || []).some(
        (product) =>
          isSameGroupId(product.group_id, groupId) &&
          product?.manually_updated_install_quantity,
      )
      if (idx === -1) {
        return [
          ...list,
          {
            id: groupId,
            group_id: groupId,
            product_marketing_quantity: Number(newValue),
            ...(hasManualInstallQty
              ? {}
              : { install_marketing_quantity: Number(newValue) }),
          },
        ]
      }
      return list.map((group, groupIndex) =>
        groupIndex !== idx
          ? group
          : {
              ...group,
              product_marketing_quantity: Number(newValue),
              ...(hasManualInstallQty
                ? {}
                : { install_marketing_quantity: Number(newValue) }),
            },
      )
    })
    setRefreshCalculation((v) => !v)
  }

  const handleGroupLineQuantityDraftChange = (productIndexId, field, value) => {
    const key = String(productIndexId)
    setGroupProductQuantityDrafts((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: normalizeGroupLineQuantity(value),
      },
    }))
  }

  const clearGroupLineQuantityDrafts = (products = []) => {
    setGroupProductQuantityDrafts((prev) => {
      const next = { ...prev }
      ;(products || []).forEach((product) => {
        delete next[String(product.indexId)]
      })
      return next
    })
  }

  const handleGroupLocationFieldChange = (groupId, locationIndex, field, value) => {
    setSelectedProducts((prevProducts) => {
      const grouped = (prevProducts || []).filter((product) =>
        isSameGroupId(product.group_id, groupId),
      )
      if (grouped.length === 0) return prevProducts

      const representative = grouped[0]
      const locationQuantity = getGroupLocationQuantity(groupId, representative)
      const sourceAddr = representative?.location || representative?.addresses || []
      let templateAddresses = buildGroupAddressesForProduct(
        { ...representative, locationQuantity },
        sourceAddr,
        {
          allLocationSame: representative?.allLocationSame,
          isSingleCard: representative?.is_single_card,
          locationQuantity,
        },
      )

      if (representative?.allLocationSame) {
        const nextBase = {
          ...(templateAddresses[0] || getBlankLocationAddress(0)),
          [field]: value,
        }
        templateAddresses = templateAddresses.map((address, index) => ({
          ...address,
          ...nextBase,
          id: index,
        }))
      } else {
        templateAddresses[locationIndex] = {
          ...(templateAddresses[locationIndex] || getBlankLocationAddress(locationIndex)),
          [field]: value,
          id: locationIndex,
        }
      }

      patchGroupLocationMeta(groupId, {
        group_addresses: templateAddresses,
        group_all_location_same: representative?.allLocationSame ? 1 : 0,
        group_is_single_card: representative?.is_single_card ? 1 : 0,
      })

      return (prevProducts || []).map((product) =>
        !isSameGroupId(product.group_id, groupId)
          ? product
          : {
              ...product,
              location: buildGroupAddressesForProduct(
                { ...product, locationQuantity },
                templateAddresses,
                {
                  allLocationSame: product?.allLocationSame,
                  isSingleCard: product?.is_single_card,
                  locationQuantity,
                },
              ),
            },
      )
    })
  }

  const handleGroupAllLocationSameChange = (groupId) => {
    setSelectedProducts((prevProducts) => {
      const grouped = (prevProducts || []).filter((product) =>
        isSameGroupId(product.group_id, groupId),
      )
      if (grouped.length === 0) return prevProducts

      const representative = grouped[0]
      const nextAllLocationSame = !representative?.allLocationSame
      const locationQuantity = getGroupLocationQuantity(groupId, representative)
      const sourceAddr = representative?.location || representative?.addresses || []
      const templateAddresses = buildGroupAddressesForProduct(
        { ...representative, locationQuantity },
        sourceAddr,
        {
          allLocationSame: nextAllLocationSame,
          isSingleCard: representative?.is_single_card,
          locationQuantity,
        },
      )

      patchGroupLocationMeta(groupId, {
        group_addresses: templateAddresses,
        group_all_location_same: nextAllLocationSame ? 1 : 0,
        group_is_single_card: representative?.is_single_card ? 1 : 0,
      })

      return (prevProducts || []).map((product) =>
        !isSameGroupId(product.group_id, groupId)
          ? product
          : {
              ...product,
              allLocationSame: nextAllLocationSame,
              location: buildGroupAddressesForProduct(
                { ...product, locationQuantity },
                templateAddresses,
                {
                  allLocationSame: nextAllLocationSame,
                  isSingleCard: product?.is_single_card,
                  locationQuantity,
                },
              ),
            },
      )
    })
  }

  const handleGroupSingleCardChange = (groupId, checked) => {
    setSelectedProducts((prevProducts) => {
      const grouped = (prevProducts || []).filter((product) =>
        isSameGroupId(product.group_id, groupId),
      )
      if (grouped.length === 0) return prevProducts

      const representative = grouped[0]
      const locationQuantity = getGroupLocationQuantity(groupId, representative)
      const sourceAddr = representative?.location || representative?.addresses || []
      const templateAddresses = buildGroupAddressesForProduct(
        { ...representative, locationQuantity },
        sourceAddr,
        {
          allLocationSame: representative?.allLocationSame,
          isSingleCard: checked,
          locationQuantity,
        },
      )

      patchGroupLocationMeta(groupId, {
        group_addresses: templateAddresses,
        group_all_location_same: representative?.allLocationSame ? 1 : 0,
        group_is_single_card: checked ? 1 : 0,
      })

      return (prevProducts || []).map((product) =>
        !isSameGroupId(product.group_id, groupId)
          ? product
          : {
              ...product,
              // Keep member locationQuantity for set-multiplier billing; only expand locations.
              is_single_card: checked,
              ...(groupQuantityMode === GROUP_QUANTITY_MODE_SET_MULTIPLIER
                ? {}
                : { locationQuantity: checked ? 1 : product.locationQuantity }),
              location: buildGroupAddressesForProduct(
                { ...product, locationQuantity },
                templateAddresses,
                {
                  allLocationSame: product?.allLocationSame,
                  isSingleCard: checked,
                  locationQuantity,
                },
              ),
            },
      )
    })
  }

  const handleGroupInstallQuantityChange = (groupId, value) => {
    let newValue = String(value ?? '')
    if (newValue.startsWith('-')) newValue = newValue.slice(1)
    newValue = newValue.replace(/[^0-9]/g, '')
    if (newValue === '' || Number(newValue) <= 0) newValue = 1
    else newValue = Number(newValue)

    if (groupQuantityMode === GROUP_QUANTITY_MODE_SET_MULTIPLIER) {
      setCustomerOrderProductGroups((prev) => {
        const list = prev || []
        const idx = list.findIndex((group) =>
          isSameGroupId(group?.id ?? group?.group_id, groupId),
        )
        if (idx === -1) {
          return [
            ...list,
            {
              id: groupId,
              group_id: groupId,
              install_marketing_quantity: Number(newValue),
              install_qty_manually_set: true,
            },
          ]
        }
        return list.map((group, groupIndex) =>
          groupIndex !== idx
            ? group
            : {
                ...group,
                install_marketing_quantity: Number(newValue),
                install_qty_manually_set: true,
              },
        )
      })
      setRefreshCalculation((v) => !v)
      return
    }

    const groupProducts = (selectedProducts || []).filter((product) =>
      isSameGroupId(product.group_id, groupId),
    )
    if (!groupProducts.length) return

    const representative = groupProducts[0]
    const sourceAddr = representative?.location || representative?.addresses || []
    const templateAddresses = buildGroupAddressesForProduct(
      { ...representative, install_quantity: newValue },
      sourceAddr,
      {
        allLocationSame: representative?.allLocationSame,
        isSingleCard: representative?.is_single_card,
      },
    )

    setSelectedProducts((prevProducts) =>
      (prevProducts || []).map((product) =>
        !isSameGroupId(product.group_id, groupId)
          ? product
          : {
              ...product,
              install_quantity: Number(newValue),
              install_marketing_quantity: Number(newValue),
              group_install_quantity: Number(newValue),
              manually_updated_install_quantity: true,
              location: buildGroupAddressesForProduct(product, templateAddresses, {
                allLocationSame: product?.allLocationSame,
                isSingleCard: product?.is_single_card,
              }),
              addresses: buildGroupAddressesForProduct(product, templateAddresses, {
                allLocationSame: product?.allLocationSame,
                isSingleCard: product?.is_single_card,
              }),
            },
      ),
    )
    setRefreshCalculation((v) => !v)
  }

  const handleGroupMarketingTypeChange = (
    groupId,
    newValue,
    totalProductFormulaEach = 0,
    totalProductRetailEach = 0,
    currentGroupAdjustment,
  ) => {
    const hasMarketingPermission = explodedPermissions?.some((permission) =>
      negativeMarketingPermissionNames.includes(permission),
    )
    const currentValues = groupProductMarketingValues?.[groupId] || {}
    const currentAdjustment = Number(currentValues.adjustment ?? currentGroupAdjustment ?? 0) || 0
    const formulaEach = Number(totalProductFormulaEach || 0)
    const retailEach = Number(totalProductRetailEach || 0)
    const maxNegative = hasMarketingPermission
      ? retailEach
      : Math.max(0, retailEach - formulaEach)

    if (newValue === false) {
      if (!hasMarketingPermission && retailEach <= formulaEach) {
        return enqueueSnackbar(
          'Total Retail Price Each cannot be lower than or equal to Total Product formula cost Each for a negative group marketing adjustment.',
          { variant: 'error' },
        )
      }
      if (Number(currentAdjustment) >= maxNegative && hasMarketingPermission) {
        return enqueueSnackbar('Retail value cannot be zero or less', { variant: 'error' })
      } else if (currentAdjustment > maxNegative && !hasMarketingPermission) {
        return enqueueSnackbar(
          `Negative Adjustment Amount cannot reduce total below Product Formula Cost. Max allowed: $${maxNegative.toFixed(2)}`,
          { variant: 'error' },
        )
      }
    }

    setGroupProductMarketingValues((prev) => ({
      ...prev,
      [groupId]: {
        adjValueType: newValue,
        adjustment: currentAdjustment,
      },
    }))
    setRefreshCalculation((v) => !v)
  }

  const handleGroupMarketingAdjustmentChange = (
    groupId,
    value,
    totalFormulaEach = 0,
    totalRetailEach = 0,
    groupAdjValueType,
  ) => {
    let newValue = String(value ?? '')
    const currentValues = groupProductMarketingValues?.[groupId] || {}
    if (newValue === '') newValue = '0'
    let numValue = parseFloat(newValue) || 0
    if (numValue < 0) numValue = 0

    const adjValueType = currentValues.adjValueType ?? groupAdjValueType ?? true
    const isNegative = adjValueType === false || Number(adjValueType) === 0
    if (isNegative) {
      const hasMarketingPermission = explodedPermissions?.some((permission) =>
        negativeMarketingPermissionNames.includes(permission),
      )
      const formulaEach = Number(totalFormulaEach || 0)
      const retailEachBase = Math.max(0, Number(totalRetailEach || 0))
      const maxNegative = hasMarketingPermission
        ? retailEachBase
        : Math.max(0, retailEachBase - formulaEach)
      if (numValue >= maxNegative && hasMarketingPermission) {
        enqueueSnackbar('Retail value cannot be zero or less', { variant: 'error' })
        return
      } else if (numValue > maxNegative && !hasMarketingPermission) {
        enqueueSnackbar(
          `Negative Adjustment Amount cannot reduce total below Product Formula Cost. Max allowed: $${maxNegative.toFixed(2)}`,
          { variant: 'error' },
        )
        return
      }
    }

    newValue = numValue.toFixed(2)
    setGroupProductMarketingValues((prev) => ({
      ...prev,
      [groupId]: {
        adjValueType: currentValues.adjValueType ?? groupAdjValueType ?? true,
        adjustment: newValue,
      },
    }))
    setRefreshCalculation((v) => !v)
  }

  const handleGroupInstallMarketingTypeChange = (
    groupId,
    newValue,
    totalInstallFormulaEach = 0,
    totalInstallRetailEach = 0,
    currentGroupInstallAdjustment,
  ) => {
    const hasMarketingPermission = explodedPermissions?.some((permission) =>
      negativeMarketingPermissionNames.includes(permission),
    )
    const currentValues = groupInstallMarketingValues?.[groupId] || {}
    const currentAdjustment =
      Number(currentValues.adjustment ?? currentGroupInstallAdjustment ?? 0) || 0
    const formulaEach = Number(totalInstallFormulaEach || 0)
    const retailEach = Number(totalInstallRetailEach || 0)
    const maxNegative = hasMarketingPermission
      ? retailEach
      : Math.max(0, retailEach - formulaEach)

    if (newValue === false) {
      if (!hasMarketingPermission && retailEach <= formulaEach) {
        return enqueueSnackbar(
          'Total Install Retail Price Each cannot be lower than or equal to Total Install formula cost Each for a negative group marketing adjustment.',
          { variant: 'error' },
        )
      }
      if (Number(currentAdjustment) >= maxNegative && hasMarketingPermission) {
        return enqueueSnackbar('Retail value cannot be zero or less', { variant: 'error' })
      } else if (currentAdjustment > maxNegative && !hasMarketingPermission) {
        return enqueueSnackbar(
          `Negative Adjustment Amount cannot reduce total below Install Formula Cost. Max allowed: $${maxNegative.toFixed(2)}`,
          { variant: 'error' },
        )
      }
    }

    setGroupInstallMarketingValues((prev) => ({
      ...prev,
      [groupId]: {
        adjValueType: newValue,
        adjustment: currentAdjustment,
      },
    }))
    setRefreshCalculation((v) => !v)
  }

  const handleGroupInstallMarketingAdjustmentChange = (
    groupId,
    value,
    totalInstallFormulaEach = 0,
    totalInstallRetailEach = 0,
    groupInstallAdjValueType,
  ) => {
    let newValue = String(value ?? '')
    const currentValues = groupInstallMarketingValues?.[groupId] || {}
    if (newValue === '') newValue = '0'
    let numValue = parseFloat(newValue) || 0
    if (numValue < 0) numValue = 0

    const adjValueType = currentValues.adjValueType ?? groupInstallAdjValueType ?? true
    const isNegative = adjValueType === false || Number(adjValueType) === 0
    if (isNegative) {
      const hasMarketingPermission = explodedPermissions?.some((permission) =>
        negativeMarketingPermissionNames.includes(permission),
      )
      const formulaEach = Number(totalInstallFormulaEach || 0)
      const retailEachBase = Math.max(0, Number(totalInstallRetailEach || 0))
      const maxNegative = hasMarketingPermission
        ? retailEachBase
        : Math.max(0, retailEachBase - formulaEach)
      if (numValue >= maxNegative && hasMarketingPermission) {
        enqueueSnackbar('Retail value cannot be zero or less', { variant: 'error' })
        return
      } else if (numValue > maxNegative && !hasMarketingPermission) {
        enqueueSnackbar(
          `Negative Adjustment Amount cannot reduce total below Install Formula Cost. Max allowed: $${maxNegative.toFixed(2)}`,
          { variant: 'error' },
        )
        return
      }
    }

    newValue = numValue.toFixed(2)
    setGroupInstallMarketingValues((prev) => ({
      ...prev,
      [groupId]: {
        adjValueType: currentValues.adjValueType ?? groupInstallAdjValueType ?? true,
        adjustment: newValue,
      },
    }))
    setRefreshCalculation((v) => !v)
  }

  const resetGroupCatalogSourceModal = () => {
    setOpenGroupCatalogSourceModal(false)
    setGroupCatalogSourceIndexId(null)
    setGroupCatalogCustomSourceId(null)
    setShowGroupCatalogCreateForm(false)
    setGroupCatalogSourceInputValue('')
    setGroupCatalogCustomName('')
    setGroupCatalogCustomDescription('')
    setGroupCatalogCreateError('')
  }

  const buildGroupFromSelection = (selectedLineCatalogProduct, overrides = {}) => {
    // Client request: remove restriction that prevents grouping when there are no catalog ("choose") rows.
    // Fallback to any selected member as the anchor for group name/notes so the group can still be created.
    let anchorProduct = selectedLineCatalogProduct
    if (!anchorProduct) {
      anchorProduct =
        (selectedProducts || []).find((p) =>
          groupSelectedProductIds?.some(
            (id) => Number(id) === Number(p?.indexId),
          ),
        ) || (selectedProducts || [])[0] || null
    }
    if (!anchorProduct) {
      enqueueSnackbar('Grouping requires at least one selected product.', { variant: 'error' })
      return false
    }

    const derivedGroupName = (
      overrides.name ?? anchorProduct?.product_name ?? anchorProduct?.name ?? ''
    ).trim()
    const derivedGroupDescription = (
      overrides.description ??
      anchorProduct?.product_desc ??
      anchorProduct?.description ??
      ''
    ).trim()

    if (!derivedGroupName) {
      enqueueSnackbar('Group name is required.', { variant: 'error' })
      return false
    }

    if (
      quotationGroupNameExists({
        name: derivedGroupName,
        customerOrderProductGroups,
        selectedProducts,
        customCatalogGroupSources,
      })
    ) {
      enqueueSnackbar('A group with this name already exists.', { variant: 'error' })
      return false
    }

    const newGroupId = Date.now()
    const anchorIndexId = anchorProduct?.indexId

    setSelectedProducts((prev) =>
      (prev || []).map((product) => {
        if (
          !groupSelectedProductIds.some(
            (id) => Number(id) === Number(product.indexId),
          )
        ) {
          return product
        }
        const memberInstallQty = product.manually_updated_install_quantity
          ? product.install_quantity || 1
          : product.locationQuantity || product.install_quantity || 1
        const groupedProduct = {
          ...product,
          ...snapshotGroupMemberMarketing(product),
          group_id: newGroupId,
          group_name: derivedGroupName,
          group_description: derivedGroupDescription,
          group_catalog_anchor_index_id: anchorIndexId,
          group_product_quantity: product.locationQuantity || 1,
          group_install_quantity: memberInstallQty,
          // Keep the flag only if install was intentionally diverged before grouping.
          manually_updated_install_quantity: Boolean(
            product.manually_updated_install_quantity &&
              Number(product.install_quantity || 0) !== Number(product.locationQuantity || 0),
          ),
          adjustment: 0,
          adj_value_type: true,
          install_adjustment: 0,
          install_adj_value_type: true,
        }

        if (groupQuantityMode === GROUP_QUANTITY_MODE_SET_MULTIPLIER) {
          return groupedProduct
        }

        return {
          ...groupedProduct,
          product_marketing_quantity: product.locationQuantity || 1,
          install_marketing_quantity: memberInstallQty,
          install_quantity: memberInstallQty,
        }
      }),
    )

    if (groupQuantityMode === GROUP_QUANTITY_MODE_SET_MULTIPLIER) {
      setCustomerOrderProductGroups((prev) => [
        ...(prev || []),
        {
          id: newGroupId,
          group_id: newGroupId,
          group_name: derivedGroupName,
          group_description: derivedGroupDescription,
          product_marketing_quantity: 1,
          install_marketing_quantity: 1,
        },
      ])
    }

    // Group-level marketing starts fresh; line adjustments stay on members (pre_group_* snapshot).
    setGroupProductMarketingValues((prev) => ({
      ...prev,
      [newGroupId]: {
        adjustment: 0,
        adjValueType: true,
      },
    }))
    setGroupInstallMarketingValues((prev) => ({
      ...prev,
      [newGroupId]: {
        adjustment: 0,
        adjValueType: true,
      },
    }))

    setGroupSelectedProductIds([])
    resetGroupCatalogSourceModal()
    return true
  }

  const handleCancelGroupCatalogCreate = () => {
    setShowGroupCatalogCreateForm(false)
    setGroupCatalogCustomName('')
    setGroupCatalogCustomDescription('')
    setGroupCatalogCreateError('')
  }

  const handleCreateCustomCatalogGroup = () => {
    const name = groupCatalogCustomName.trim()
    const description = groupCatalogCustomDescription.trim()

    if (!name) {
      setGroupCatalogCreateError('Group name is required.')
      return
    }

    if (
      quotationGroupNameExists({
        name,
        customerOrderProductGroups,
        selectedProducts,
        customCatalogGroupSources,
      })
    ) {
      setGroupCatalogCreateError('A group with this name already exists.')
      return
    }

    const customSource = {
      id: `custom-${Date.now()}`,
      isCustomGroupSource: true,
      name,
      description,
    }

    const anchorProduct = getSelectedLineCatalogProducts(selectedProducts, groupSelectedProductIds)[0]
    const success = buildGroupFromSelection(anchorProduct, { name, description })
    if (success) {
      setCustomCatalogGroupSources((prev) => [...prev, customSource])
    }
  }

  const toggleGroupProductSelection = (product) => {
    if (!product?.indexId || !isQuotationChooseCatalogRow(product)) return
    setGroupSelectedProductIds((prev) => {
      const alreadySelected = (prev || []).some((id) => Number(id) === Number(product.indexId))
      if (alreadySelected) {
        return (prev || []).filter((id) => Number(id) !== Number(product.indexId))
      }
      return [...(prev || []), product.indexId]
    })
  }

  const handleCreateGroupFromSelection = () => {
    if (groupSelectedProductIds.length < 2) return
    const selectedLineCatalogCount = getSelectedLineCatalogCount(
      selectedProducts,
      groupSelectedProductIds,
    )
    // if (selectedLineCatalogCount === 0) {
    //   enqueueSnackbar('A group must contain at least one catalog line item.', { variant: 'error' })
    //   return
    // }

    const selectedLineCatalogProducts = getCatalogSourceOptions({
      selectedProducts,
      groupSelectedProductIds,
    })
    if (selectedLineCatalogProducts.length >= 1) {
      setGroupCatalogSourceIndexId(null)
      setGroupCatalogCustomSourceId(null)
      setShowGroupCatalogCreateForm(false)
      setGroupCatalogSourceInputValue('')
      setGroupCatalogCustomName('')
      setGroupCatalogCustomDescription('')
      setGroupCatalogCreateError('')
      setOpenGroupCatalogSourceModal(true)
      return
    }

    buildGroupFromSelection(selectedLineCatalogProducts?.[0])
  }

  const resetGroupMarketingAdjustments = useCallback((groupId) => {
    const key = String(groupId)
    setGroupProductMarketingValues((prev) => {
      const next = { ...prev }
      delete next[groupId]
      delete next[key]
      return next
    })
    setGroupInstallMarketingValues((prev) => {
      const next = { ...prev }
      delete next[groupId]
      delete next[key]
      return next
    })
    setRefreshCalculation((v) => !v)
  }, [setRefreshCalculation])

  const handleAddToExistingGroup = () => {
    if (!addToGroupId) return

    const groupMeta =
      (customerOrderProductGroups || []).find((g) => String(g?.id) === String(addToGroupId)) ||
      (groupedProducts || []).find((g) => String(g.id) === String(addToGroupId))
    const group = groupMeta
    if (!group) return

    setSelectedProducts((prev) => {
      const existingBeforeAdd = (prev || []).filter(
        (p) => isSameGroupId(p.group_id, group.id) && !groupSelectedProductIds.includes(p.indexId),
      )
      const inheritedAnchor =
        existingBeforeAdd.find((p) => p?.group_catalog_anchor_index_id != null)
          ?.group_catalog_anchor_index_id ?? existingBeforeAdd[0]?.group_catalog_anchor_index_id

      let nextProducts = (prev || []).map((product) => {
        if (!groupSelectedProductIds.includes(product.indexId)) return product
        const memberInstallQty = product.install_quantity || 1
        const groupedProduct = withInheritedGroupNotes(
          {
            ...product,
            ...snapshotGroupMemberMarketing(product),
            group_id: group.id,
            group_name: group.group_name || group.name || 'Group',
            group_description: group.group_description || group.description || '',
            group_catalog_anchor_index_id:
              inheritedAnchor != null ? inheritedAnchor : product.group_catalog_anchor_index_id,
            group_product_quantity: product.locationQuantity || 1,
            group_install_quantity: memberInstallQty,
            adjustment: 0,
            adj_value_type: true,
            install_adjustment: 0,
            install_adj_value_type: true,
          },
          existingBeforeAdd,
        )

        if (groupQuantityMode === GROUP_QUANTITY_MODE_SET_MULTIPLIER) {
          return groupedProduct
        }

        return {
          ...groupedProduct,
          product_marketing_quantity: product.locationQuantity || 1,
          install_marketing_quantity: memberInstallQty,
        }
      })

      const existingGroupProducts = nextProducts.filter((p) => isSameGroupId(p.group_id, group.id))
      const representative =
        existingGroupProducts.find((p) => !groupSelectedProductIds.includes(p.indexId)) ||
        existingGroupProducts[0]

      if (representative && groupQuantityMode !== GROUP_QUANTITY_MODE_SET_MULTIPLIER) {
        const targetQuantity = Number(representative.locationQuantity || 1)
        const targetInstallQuantity = Number(representative.install_quantity || 1)
        const sourceAddr = representative.location || representative.addresses || []
        const templateAddresses = buildGroupAddressesForProduct(
          { ...representative, locationQuantity: targetQuantity },
          sourceAddr,
          {
            allLocationSame: representative.allLocationSame,
            isSingleCard: representative.is_single_card,
          },
        )

        nextProducts = nextProducts.map((product) => {
          if (!groupSelectedProductIds.includes(product.indexId)) return product
          return {
            ...product,
            locationQuantity: targetQuantity,
            install_quantity: targetInstallQuantity,
            product_marketing_quantity: targetQuantity,
            install_marketing_quantity: targetInstallQuantity,
            location: buildGroupAddressesForProduct(product, templateAddresses, {
              allLocationSame: product.allLocationSame,
              isSingleCard: product.is_single_card,
            }),
            addresses: buildGroupAddressesForProduct(product, templateAddresses, {
              allLocationSame: product.allLocationSame,
              isSingleCard: product.is_single_card,
            }),
          }
        })
      }

      return nextProducts
    })

    setOpenAddToGroupModal(false)
    setAddToGroupId('')
    setGroupSelectedProductIds([])
  }

  const handleDeleteGroup = (groupId) => {
    const sid = String(groupId)
    const deletedGroupNameKey = normalizeGroupNameKey(
      (selectedProducts || []).find((p) => String(p.group_id) === sid)?.group_name,
    )
    const filtered = (selectedProducts || []).filter((p) => String(p.group_id) !== sid)
    const sorted = [...filtered].sort((a, b) => (a?.indexId || 0) - (b?.indexId || 0))
    const indexIdMap = {}
    sorted.forEach((p, idx) => {
      indexIdMap[String(p.indexId)] = idx + 1
    })
    remapProductIndexIds(indexIdMap, filtered)

    const cleanMarketing = (prev) => {
      const next = { ...prev }
      delete next[groupId]
      delete next[String(groupId)]
      return next
    }
    setGroupProductMarketingValues(cleanMarketing)
    setGroupInstallMarketingValues(cleanMarketing)
    setGroupEditMode({})
    setGroupSelectedProductIds([])
    setCustomerOrderProductGroups((prev) =>
      (prev || []).filter((g) => String(g?.id ?? g?.group_id) !== sid),
    )
    if (deletedGroupNameKey) {
      setCustomCatalogGroupSources((prev) =>
        (prev || []).filter(
          (source) => normalizeGroupNameKey(source?.name) !== deletedGroupNameKey,
        ),
      )
    }
    setRefreshCalculation((v) => !v)
  }

  /** Clear install pricing on every member of a group (group Install Price row delete). */
  const handleDeleteGroupInstall = useCallback(
    (groupId) => {
      const sid = String(groupId)
      setSelectedProducts((prev) =>
        (prev || []).map((product) => {
          if (
            !isSameGroupId(
              product?.group_id ?? product?.customer_order_product_group_id,
              groupId,
            )
          ) {
            return product
          }
          const cleared = clearCatalogInstallCost(product)
          return {
            ...cleared,
            retail_price: cleared.actual_retail_price,
          }
        }),
      )
      setGroupInstallMarketingValues((prev) => {
        const next = { ...prev }
        delete next[groupId]
        delete next[sid]
        return next
      })
      setCustomerOrderProductGroups((prev) =>
        (prev || []).map((g) => {
          if (String(g?.id ?? g?.group_id) !== sid) return g
          return {
            ...g,
            install_marketing_adjustment: 0,
            install_marketing_adj_value_type: true,
            installMarketingAdjustment: 0,
            installMarketingAdjValueType: true,
          }
        }),
      )
      setRefreshCalculation((v) => !v)
    },
    [setSelectedProducts, setRefreshCalculation],
  )

  const getGroupLosingMoneyFlagsForGroup = (group) =>
    getGroupLosingMoneyFlags(group, groupProductMarketingValues, groupInstallMarketingValues)

  const getLosingMoneyRowSxForGroup = (isLosingMoneyItem) =>
    getLosingMoneyRowSx(theme, isLosingMoneyItem)

  const getCatalogSourceDropdownOptionsForModal = (inputValue = '') =>
    getCatalogSourceDropdownOptions({
      selectedProducts,
      groupSelectedProductIds,
      customCatalogGroupSources,
      customerOrderProductGroups,
      inputValue,
    })

  return {
    openGroupedRows,
    setOpenGroupedRows,
    groupEditMode,
    setGroupEditMode,
    groupLocationStates,
    setGroupLocationStates,
    groupSelectedProductIds,
    setGroupSelectedProductIds,
    groupProductQuantityDrafts,
    groupProductMarketingValues,
    groupInstallMarketingValues,
    addToGroupId,
    setAddToGroupId,
    openAddToGroupModal,
    setOpenAddToGroupModal,
    openGroupCatalogSourceModal,
    groupCatalogSourceIndexId,
    setGroupCatalogSourceIndexId,
    groupCatalogCustomSourceId,
    setGroupCatalogCustomSourceId,
    customCatalogGroupSources,
    groupCatalogSourceInputValue,
    setGroupCatalogSourceInputValue,
    showGroupCatalogCreateForm,
    setShowGroupCatalogCreateForm,
    groupCatalogCustomName,
    setGroupCatalogCustomName,
    groupCatalogCustomDescription,
    setGroupCatalogCustomDescription,
    groupCatalogCreateError,
    setGroupCatalogCreateError,
    customerOrderProductGroups,
    setCustomerOrderProductGroups,
    groupNoteTargets,
    openGroupProductNoteIndex,
    setOpenGroupProductNoteIndex,
    groupInstallNoteTargets,
    openGroupInstallNoteIndex,
    setOpenGroupInstallNoteIndex,
    groupedProducts,
    groupedProductsMap,
    productTableBodyRenderOrder,
    addToGroupOptions,
    getGroupMemberIndexIdsByProductIndexId,
    getProductAttachmentsList,
    clearGroupSelectionForProduct,
    remapGroupStateOnIndexIdChange,
    handleOpenGroupNote,
    handleOpenGroupInstallNote,
    handleGroupFieldChange,
    handleGroupQuantityChange,
    handleGroupLineQuantityDraftChange,
    clearGroupLineQuantityDrafts,
    handleGroupLocationFieldChange,
    handleGroupAllLocationSameChange,
    handleGroupSingleCardChange,
    handleGroupInstallQuantityChange,
    handleGroupMarketingTypeChange,
    handleGroupMarketingAdjustmentChange,
    handleGroupInstallMarketingTypeChange,
    handleGroupInstallMarketingAdjustmentChange,
    resetGroupCatalogSourceModal,
    buildGroupFromSelection,
    handleCancelGroupCatalogCreate,
    handleCreateCustomCatalogGroup,
    toggleGroupProductSelection,
    handleCreateGroupFromSelection,
    resetGroupMarketingAdjustments,
    handleAddToExistingGroup,
    handleDeleteGroup,
    handleDeleteGroupInstall,
    getGroupLosingMoneyFlags: getGroupLosingMoneyFlagsForGroup,
    getLosingMoneyRowSx: getLosingMoneyRowSxForGroup,
    getCatalogSourceDropdownOptions: getCatalogSourceDropdownOptionsForModal,
    getSelectedLineCatalogProducts: (selectedIds) =>
      getSelectedLineCatalogProducts(selectedProducts, selectedIds),
    getSelectedLineCatalogCount: (selectedIds) =>
      getSelectedLineCatalogCount(selectedProducts, selectedIds),
    getGroupAnchorIndexId,
    hasMarkupPermission,
    markupType,
    theme,
    enqueueSnackbar,
    setSelectedProducts,
    setRefreshCalculation,
  }
}

export default useQuotationProductGrouping
